import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { jsonAuthHeaders } from "@/utils/headers";

interface DeleteVibeChatSessionResponse {
  success: boolean;
  message?: string;
}

interface DeleteAllVibeChatSessionsResponse {
  success: boolean;
  deleted_count: number;
}

export const useDeleteVibeChatSession = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string): Promise<DeleteVibeChatSessionResponse> => {
      if (!auth?.userToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(ENDPOINTS.delete_vibe_chat, {
        method: "DELETE",
        headers: jsonAuthHeaders(auth.userToken),
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Session not found");
        }
        throw new Error("Failed to delete chat session");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recent-vibe-chat-sessions"] });
    },
  });
};

export const useDeleteAllVibeChatSessions = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<DeleteAllVibeChatSessionsResponse> => {
      if (!auth?.userToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(ENDPOINTS.delete_all_vibe_chat, {
        method: "DELETE",
        headers: jsonAuthHeaders(auth.userToken),
      });

      if (!response.ok) {
        throw new Error("Failed to delete all chat sessions");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recent-vibe-chat-sessions"] });
    },
  });
};
